import type { ReactNode } from "react";
import type { GameState } from "../api";
import { closeApp } from "../telegram";

interface Props {
  state: GameState<unknown>;
  title: string;
  subtitle: string;
  /** Game-specific block (answer flag, etc.). */
  children?: ReactNode;
  scoreLabel: string;
  formatScore: (s: number | null) => string;
  onDismiss: () => void;
  /** Archive replay: no chips post, no streak. */
  archive?: boolean;
}

export function ResultSheet({ state, title, subtitle, children, scoreLabel, formatScore, onDismiss, archive }: Props) {
  const winPct = state.me.games ? Math.round((state.me.wins / state.me.games) * 100) : 0;
  return (
    <div className="fixed inset-0 z-30 flex items-end justify-center bg-black/50" onClick={onDismiss}>
      <div
        className="sheet max-h-[88vh] w-full max-w-[520px] overflow-y-auto rounded-t-2xl bg-tgsection px-5 pb-[calc(var(--tg-safe-area-inset-bottom,0px)+20px)] pt-3 text-tgtext shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-[rgba(128,128,128,0.4)]" />
        <div className="text-center">
          <div className="text-2xl font-bold">{title}</div>
          <div className="mt-1 text-sm text-tghint">{subtitle}</div>
        </div>
        {children}
        <div className="mt-5 grid grid-cols-4 gap-2 text-center">
          <Stat value={state.me.games} label="ігор" />
          <Stat value={winPct} label="% розгадано" />
          <Stat value={state.me.streak} label="стрік 🔥" />
          <Stat value={state.me.avgScore ?? "–"} label={scoreLabel} />
        </div>
        {state.group.length > 0 && (
          <div className="mt-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-tghint">сьогодні в чаті</div>
            <ul className="mt-2 divide-y divide-[rgba(128,128,128,0.2)] text-sm">
              {state.group.map((g) => (
                <li key={g.userId} className="flex items-center justify-between py-1.5">
                  <span className="font-medium">{g.name}</span>
                  <span className="text-tghint">
                    {g.status === "won" ? formatScore(g.score) : g.status === "lost" ? "✗" : "грає…"}
                    {g.streak > 0 ? ` · 🔥${g.streak}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="mt-5 text-center text-xs text-tghint">
          {archive ? "📼 архівна гра — без стріка і посту в чат" : "чіпс уже несе результат у чат"}
        </div>
        <button
          type="button"
          onClick={closeApp}
          className="mt-3 w-full rounded-xl bg-tgbutton py-3 text-sm font-semibold text-tgbuttontext active:opacity-80"
        >
          назад у чат
        </button>
      </div>
    </div>
  );
}

function Stat({ value, label }: { value: number | string; label: string }) {
  return (
    <div>
      <div className="text-2xl font-bold leading-none">{value}</div>
      <div className="mt-1 text-[11px] leading-tight text-tghint">{label}</div>
    </div>
  );
}
